import { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Flex,
  HStack,
  Button,
  FormControl,
  FormLabel,
  Input,
  Grid,
  GridItem,
  useToast,
} from '@chakra-ui/react';
import {
  NestedHome,
  InsertAddress,
  NestedUser,
  CategoryType,
} from '@habity/types';
import { useHomes } from '~/hooks/useHomes';
import { EditButton } from '~/components/ui/EditButton';
import { isAdmin } from '~/lib/roles';
import { isHomeOwner, isSellerClaimed } from '@habity/services';
import { HomeGeneralData } from './GeneralData';

interface StructuralDataEditorProps {
  home: NestedHome;
  user: NestedUser | null;
}

export const StructuralDataEditor = ({
  home,
  user,
}: StructuralDataEditorProps) => {
  const { address } = home || {};
  const [isEditing, setIsEditing] = useState(false);
  const [livingSpace, setLivingSpace] = useState<number | undefined>(
    address?.living_space || undefined
  );
  const [rooms, setRooms] = useState<number | undefined>(
    address?.rooms || undefined
  );
  const [bedrooms, setBedrooms] = useState<number | undefined>(
    address?.bedrooms || undefined
  );
  const [plotSize, setPlotSize] = useState<number | undefined>(
    address?.plot_size || undefined
  );
  const [buildYear, setBuildYear] = useState<number | undefined>(
    address?.build_year || undefined
  );
  const [isLoading, setIsLoading] = useState(false);

  const { updateAddress } = useHomes();
  const toast = useToast();

  // Reset state when home data changes
  useEffect(() => {
    setLivingSpace(address?.living_space || undefined);
    setRooms(address?.rooms || undefined);
    setBedrooms(address?.bedrooms || undefined);
    setPlotSize(address?.plot_size || undefined);
    setBuildYear(address?.build_year || undefined);
  }, [address]);

  // Handle keyboard shortcuts for save and cancel
  useEffect(() => {
    if (!isEditing) return;

    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Save on Enter key (without modifiers) or Ctrl+Enter
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        // Don't trigger if the event came from an input element
        !(event.target instanceof HTMLInputElement)
      ) {
        event.preventDefault();
        handleSave();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [isEditing]);

  const canEdit = user && (isAdmin(user) || isHomeOwner(home, user));

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Get the address ID from the home object
      const addressId = home?.address?.id;
      if (!addressId) {
        throw new Error('Address ID not found');
      }

      // Create an update object for the address fields
      // Only include fields that have been changed
      const updateData: Partial<InsertAddress> = {};

      if (livingSpace !== undefined) updateData.living_space = livingSpace;
      if (rooms !== undefined) updateData.rooms = rooms;
      if (bedrooms !== undefined) updateData.bedrooms = bedrooms;
      if (plotSize !== undefined) updateData.plot_size = plotSize;
      if (buildYear !== undefined) updateData.build_year = buildYear;

      // Update the address
      const { error } = await updateAddress(addressId, updateData);

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Uppdaterad',
        description: 'Bostadens strukturella data har uppdaterats',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Exit edit mode
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update structural data:', error);
      toast({
        title: 'Ett fel uppstod',
        description: 'Kunde inte uppdatera bostadens data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setLivingSpace(address?.living_space || undefined);
    setRooms(address?.rooms || undefined);
    setBedrooms(address?.bedrooms || undefined);
    setPlotSize(address?.plot_size || undefined);
    setBuildYear(address?.build_year || undefined);
    setIsEditing(false);
  };

  // Helper function to check if a field should be shown based on category
  const shouldShowField = (field: keyof typeof address): boolean => {
    const category = home?.address?.category;

    // Fields that should be hidden for specific categories
    const fieldsToHide: Record<string, CategoryType[]> = {
      plot_size: [
        'apartment',
        'townhouse',
        'twinhouse',
        'terraced',
        'commercial',
      ],
    };

    // If the field has category restrictions and the current category is in the hide list
    if (fieldsToHide[field] && fieldsToHide[field].includes(category)) {
      return false;
    }

    // Otherwise, show the field if it has a value in the address
    return address?.[field] !== undefined;
  };

  // Display the structural data in a grid layout
  if (isEditing) {
    return (
      <Box
        width="100%"
        py={{ mobile: '10', desktop: '20' }}
        borderBottomWidth="1px"
        borderBottomColor="rgba(0,0,0,0.2)"
      >
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Text fontSize="lg" fontWeight="medium">
            Bostadens fakta
          </Text>
        </Flex>

        <Grid
          templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
          gap={6}
        >
          {/* Living space - show for all categories */}
          {shouldShowField('living_space') && (
            <GridItem>
              <FormControl>
                <FormLabel>Boarea (m²)</FormLabel>
                <Input
                  type="number"
                  value={livingSpace || ''}
                  onChange={(e) =>
                    setLivingSpace(
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder="Boarea i kvadratmeter"
                />
              </FormControl>
            </GridItem>
          )}

          {/* Rooms - show for all categories */}
          {shouldShowField('rooms') && (
            <GridItem>
              <FormControl>
                <FormLabel>Antal rum</FormLabel>
                <Input
                  type="number"
                  value={rooms || ''}
                  onChange={(e) =>
                    setRooms(
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder="Antal rum"
                  step={0.5}
                />
              </FormControl>
            </GridItem>
          )}

          {/* Bedrooms - show for all categories */}
          {shouldShowField('bedrooms') && (
            <GridItem>
              <FormControl>
                <FormLabel>Antal sovrum</FormLabel>
                <Input
                  type="number"
                  value={bedrooms || ''}
                  onChange={(e) =>
                    setBedrooms(
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder="Antal sovrum"
                />
              </FormControl>
            </GridItem>
          )}

          {/* Plot size - hide for apartments, townhouses, etc. */}
          {shouldShowField('plot_size') && (
            <GridItem>
              <FormControl>
                <FormLabel>Tomtstorlek (m²)</FormLabel>
                <Input
                  type="number"
                  value={plotSize || ''}
                  onChange={(e) =>
                    setPlotSize(
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder="Tomtstorlek i kvadratmeter"
                />
              </FormControl>
            </GridItem>
          )}

          {/* Build year - show for all categories */}
          {shouldShowField('build_year') && (
            <GridItem>
              <FormControl>
                <FormLabel>Byggår</FormLabel>
                <Input
                  type="number"
                  value={buildYear || ''}
                  onChange={(e) =>
                    setBuildYear(
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  placeholder="Byggår"
                />
              </FormControl>
            </GridItem>
          )}
        </Grid>

        <HStack spacing="2" mt="6">
          <Button
            colorScheme="brand"
            isLoading={isLoading}
            onClick={handleSave}
          >
            Spara
          </Button>
          <Button variant="ghost" onClick={handleCancel}>
            Avbryt
          </Button>
        </HStack>
      </Box>
    );
  }

  // Only show edit button if user is admin or home owner and home status is seller_claimed
  const showEditButton = canEdit && isSellerClaimed(home);

  // Use the actual HomeGeneralData component when not editing
  return (
    <Box width="100%" position="relative">
      <HomeGeneralData home={home} />

      {showEditButton && (
        <Box position="absolute" bottom="24" right="4">
          <EditButton onClick={() => setIsEditing(true)} />
        </Box>
      )}
    </Box>
  );
};
